import { useState, useCallback, useEffect } from 'react';
import eventsServiceInstance, { type EventWithCircuit } from '../api/eventsService';
import seasonsServiceInstance, { type Season } from '../api/seasonsService';
import { isEventActive } from '../utils/eventSource';

/**
 * Hook to fetch and resolve the currently active event.
 * An event is active if today (in the circuit's timezone) falls within start_date and end_date.
 * Searches seasons (current year ± 1) and uses isEventActive for consistency with the rest of the app.
 */
export function useActiveEvent(): {
  activeEvent: EventWithCircuit | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [activeEvent, setActiveEvent] = useState<EventWithCircuit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActiveEvent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const seasonsResponse = await seasonsServiceInstance.getAllSeasons();
      if (!seasonsResponse.success || !seasonsResponse.data) {
        setError('Failed to load seasons');
        setActiveEvent(null);
        return;
      }

      const currentYear = new Date().getFullYear();
      const sortedSeasons = [...(seasonsResponse.data as Season[])].sort((a, b) => b.year - a.year);

      for (const season of sortedSeasons) {
        if (Math.abs(season.year - currentYear) > 1) continue;

        const eventsResponse = await eventsServiceInstance.getAllEvents(season.year);
        if (!eventsResponse.success || !eventsResponse.data) continue;

        const events = eventsResponse.data as EventWithCircuit[];
        const found = events.find((e) => e.status === 'Current' || isEventActive(e));
        if (found) {
          setActiveEvent(found);
          setLoading(false);
          return;
        }
      }

      setActiveEvent(null);
    } catch (err) {
      console.error('Error fetching active event:', err);
      setError(err instanceof Error ? err.message : 'Failed to load active event');
      setActiveEvent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveEvent();
  }, [fetchActiveEvent]);

  return { activeEvent, loading, error, refresh: fetchActiveEvent };
}
